<template>
  <!-- The New retainer dialog (lib/view/retainers/create_retainer_dialog.dart): the Type choice
       (Hour bank "Hours each cycle, extras billed", Fixed monthly "A set scope, a set price",
       Prepaid deposit "Money held, drawn down"), then the sections that type shows (Allowance,
       Scope and price, Deposit, Extra and unused hours) and Billing and agreement (Autopay / Pay
       link, Send automatically / Review drafts first, Linked contract (optional)). Labels are
       app_en.arb retNew*, retKind*, retField*, retSection*, recurringCollection*; names and
       amounts are sample data. Each step picks a type or opens billing; the server renders
       `billing`. -->
  <div class="rp rcw" :class="`rcw--${state}`" aria-hidden="true">
    <header class="rcw-head">
      <b>New retainer</b>
      <small>A billing plan with an allowance. The client agrees to the terms when they pay the first invoice.</small>
    </header>
    <div class="rcw-body">
      <span class="rcw-label">Type</span>
      <div class="rcw-types">
        <span v-for="t in types" :key="t.key" class="rcw-type" :class="{ 'is-on': kind === t.key }">
          <b>{{ t.name }}</b><small>{{ t.hint }}</small>
        </span>
      </div>

      <div class="rcw-stack">
        <section class="rcw-panel" :class="{ 'is-off': state !== 'hours' }">
          <span class="rcw-label">Allowance</span>
          <div class="rcw-grid">
            <span class="rcw-field"><small>Hours each cycle</small><b class="rp-num" translate="no">30 h</b></span>
            <span class="rcw-field"><small>Price each cycle</small><b class="rp-num" translate="no">$4,500.00</b></span>
            <span class="rcw-field"><small>Every</small><b>Month</b></span>
          </div>
          <span class="rcw-label">Extra and unused hours</span>
          <div class="rcw-grid">
            <span class="rcw-field"><small>Extra hours</small><b>Bill on the next invoice</b></span>
            <span class="rcw-field"><small>Support rate</small><b class="rp-num" translate="no">$150.00/h</b></span>
            <span class="rcw-field"><small>Unused hours</small><b class="rp-num">Roll over, up to 10 h</b></span>
          </div>
        </section>

        <section class="rcw-panel" :class="{ 'is-off': state !== 'fixed' }">
          <span class="rcw-label">Scope and price</span>
          <span class="rcw-field rcw-wide"><small>Scope</small><b translate="no">Site care: updates, fixes and a monthly report</b></span>
          <div class="rcw-grid">
            <span class="rcw-field"><small>Price each cycle</small><b class="rp-num" translate="no">$1,200.00</b></span>
            <span class="rcw-field"><small>Every</small><b>Month</b></span>
            <span class="rcw-field"><small>Starts</small><b class="rp-num" translate="no">Nov 1, 2026</b></span>
          </div>
        </section>

        <section class="rcw-panel" :class="{ 'is-off': state !== 'prepaid' }">
          <span class="rcw-label">Deposit</span>
          <div class="rcw-grid">
            <span class="rcw-field"><small>Deposit</small><b class="rp-num" translate="no">$10,000.00</b></span>
            <span class="rcw-field"><small>Top up below</small><b class="rp-num" translate="no">$2,000.00</b></span>
            <span class="rcw-field"><small>Top-up amount</small><b class="rp-num" translate="no">$5,000.00</b></span>
          </div>
          <span class="rcw-check"><i class="rcw-box"><svg viewBox="0 0 24 24"><path :d="check" /></svg></i>Send the top-up invoice automatically</span>
        </section>

        <section class="rcw-panel" :class="{ 'is-off': state && state !== 'billing' }">
          <span class="rcw-label">Billing and agreement</span>
          <div class="rcw-seg"><span class="is-on">Autopay</span><span>Pay link</span></div>
          <small class="rcw-hint" translate="no">Dana pays the first invoice and agrees to monthly charges.</small>
          <div class="rcw-grid rcw-grid--2">
            <span class="rcw-field"><small>Sending</small><b>Review drafts first</b></span>
            <span class="rcw-field"><small>Alerts</small><b class="rp-num">At 80% and 100%</b></span>
          </div>
          <span class="rcw-field rcw-wide"><small>Linked contract (optional)</small><b translate="no">Design services agreement · signed Oct 1</b></span>
        </section>
      </div>
    </div>
    <footer class="rcw-foot">
      <small translate="no">Nothing is charged until Dana pays the first invoice.</small>
      <span class="rp-btn">Create retainer</span>
    </footer>
  </div>
</template>

<script setup>
const props = defineProps({
  // hours | fixed | prepaid | billing
  state: { type: String, default: "billing" },
});
const types = [
  { key: "hours", name: "Hour bank", hint: "Hours each cycle, extras billed" },
  { key: "fixed", name: "Fixed monthly", hint: "A set scope, a set price" },
  { key: "prepaid", name: "Prepaid deposit", hint: "Money held, drawn down" },
];
// Billing is shown for an hour bank, so its type stays picked.
const kind = computed(() => (props.state === "fixed" || props.state === "prepaid" ? props.state : "hours"));
const check = "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z";
</script>

<style scoped>
.rcw { display: grid; grid-template-rows: auto 1fr auto; container-type: inline-size; }
.rcw-head { display: grid; gap: 2px; border-bottom: 1px solid var(--rp-line); background: var(--rp-page); padding: 12px 14px; }
.rcw-head b { font-size: 14px; font-weight: 600; }
.rcw-head small { color: var(--rp-muted); font-size: 11.5px; }
.rcw-body { display: grid; align-content: start; gap: 7px; padding: 12px 14px; }
.rcw-label { color: var(--rp-muted); font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.rcw-types { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.rcw-type { display: grid; gap: 1px; border: 1px solid var(--rp-line); border-radius: 8px; background: var(--rp-card); padding: 8px 10px; transition: border-color 0.25s ease, box-shadow 0.25s ease; }
.rcw-type b { font-size: 12.5px; font-weight: 600; }
.rcw-type small { color: var(--rp-muted); font-size: 11px; line-height: 1.3; }
.rcw-type.is-on { border-color: var(--rp-text); box-shadow: inset 0 0 0 1px var(--rp-text); }
.rcw-stack { display: grid; margin-top: 4px; }
.rcw-panel { grid-area: 1 / 1; display: grid; align-content: start; gap: 7px; transition: opacity 0.3s ease, transform 0.35s cubic-bezier(0.33, 1, 0.68, 1); }
.rcw-panel.is-off { opacity: 0; transform: translateY(6px); visibility: hidden; transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s 0.2s; }
.rcw-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.rcw-grid--2 { grid-template-columns: repeat(2, 1fr); }
.rcw-field { display: grid; gap: 1px; min-width: 0; border: 1px solid var(--rp-line); border-radius: 7px; background: var(--rp-card); padding: 6px 9px; }
.rcw-field small { color: var(--rp-muted); font-size: 10.5px; }
.rcw-field b { overflow: hidden; font-size: 12.5px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.rcw-check { display: flex; align-items: center; gap: 8px; font-size: 12.5px; }
.rcw-box { display: grid; place-items: center; width: 16px; height: 16px; border-radius: 4px; background: var(--rp-ink); color: var(--rp-ink-text); }
.rcw-box svg { width: 12px; height: 12px; fill: currentColor; }
.rcw-seg { display: grid; grid-template-columns: 1fr 1fr; gap: 2px; border-radius: 7px; background: var(--rp-grey-fill); padding: 2px; font-size: 12px; font-weight: 600; text-align: center; }
.rcw-seg span { border-radius: 5px; padding: 5px 0; color: var(--rp-muted); }
.rcw-seg .is-on { background: var(--rp-card); color: var(--rp-text); box-shadow: 0 1px 2px rgb(0 0 0 / 0.12); }
.rcw-hint { color: var(--rp-muted); font-size: 11.5px; }
.rcw-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; border-top: 1px solid var(--rp-line); background: var(--rp-page); padding: 10px 14px; }
.rcw-foot small { color: var(--rp-muted); font-size: 11.5px; }
@container (max-width: 400px) {
  .rcw-head small { display: none; }
  .rcw-type { padding: 7px 8px; }
  .rcw-type small { display: none; }
  .rcw-grid { grid-template-columns: 1fr 1fr; }
  .rcw-grid > :nth-child(3) { grid-column: 1 / -1; }
  .rcw-grid--2 > :nth-child(3) { grid-column: auto; }
  .rcw-foot small { display: none; }
  .rcw-foot .rp-btn { flex: 1; justify-content: center; }
}
@media (prefers-reduced-motion: reduce) { .rcw * { transition: none !important; } }
</style>
